/**
 * WebhookVolumeChart - Gráfico de volume de requisições por hora
 * 
 * Exibe um gráfico de barras empilhadas mostrando o volume de requisições
 * de webhook nas últimas 24 horas, diferenciando sucesso e erro.
 * 
 * @author Sistema
 * @version 1.0.0
 * @release 2026-01-20
 */

import React from 'react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  ResponsiveContainer, 
  Cell 
} from 'recharts';
import { BarChart3, Activity, CheckCircle2, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent, 
  ChartLegend, 
  ChartLegendContent,
  type ChartConfig 
} from '@/components/ui/chart';

/**
 * Interface para dados de volume por hora
 */
export interface WebhookVolumeData {
  hora: number;
  data_hora: string;
  total: number;
  sucesso: number;
  erro: number;
}

/**
 * Props do componente
 */
interface WebhookVolumeChartProps {
  /** Dados de volume por hora */
  data: WebhookVolumeData[];
  /** Estado de carregamento */
  isLoading?: boolean;
}

/**
 * Configuração de cores do gráfico
 */
const chartConfig: ChartConfig = {
  sucesso: {
    label: 'Sucesso',
    color: 'hsl(142 76% 36%)', // Verde
  },
  erro: {
    label: 'Erro',
    color: 'hsl(0 84% 60%)', // Vermelho
  },
};

/**
 * Componente principal do gráfico de volume
 */
export function WebhookVolumeChart({ data, isLoading }: WebhookVolumeChartProps) {
  // Estado de loading - exibe shimmer
  if (isLoading) {
    return (
      <div className="rounded-xl border bg-gradient-to-br from-background via-background to-muted/20 p-6 shadow-sm">
        {/* Header com shimmer */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-muted animate-pulse" />
            <div className="space-y-2">
              <div className="h-4 w-40 bg-muted animate-pulse rounded" />
              <div className="h-3 w-32 bg-muted animate-pulse rounded" />
            </div>
          </div>
          <div className="flex gap-2">
            <div className="h-6 w-16 bg-muted animate-pulse rounded-full" />
            <div className="h-6 w-16 bg-muted animate-pulse rounded-full" />
          </div>
        </div>
        
        {/* Área do gráfico com shimmer */}
        <div className="h-[200px] w-full bg-muted/30 animate-pulse rounded-lg" />
      </div>
    );
  }

  // Formata os dados para exibição no gráfico
  const chartData = [...(data || [])]
    .sort((a, b) => new Date(a.data_hora).getTime() - new Date(b.data_hora).getTime())
    .map(item => ({
      ...item,
      horaLabel: `${String(item.hora).padStart(2, '0')}h`,
    }));

  // Verifica se há dados
  const hasData = chartData.some(item => item.total > 0);

  // Calcula totais para badges
  const totalSuccess = chartData.reduce((acc, item) => acc + Number(item.sucesso), 0);
  const totalError = chartData.reduce((acc, item) => acc + Number(item.erro), 0);

  // Estado vazio - sem dados
  if (!hasData) {
    return (
      <div className="rounded-xl border bg-gradient-to-br from-background via-background to-muted/20 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
            <BarChart3 className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Volume de Requisições</h3>
            <p className="text-xs text-muted-foreground">Últimas 24 horas por hora</p>
          </div>
        </div>
        
        <div className="flex flex-col items-center justify-center h-[200px] text-center">
          <div className="p-4 rounded-full bg-muted/50 mb-4">
            <Activity className="h-8 w-8 text-muted-foreground/50" />
          </div>
          <p className="text-muted-foreground font-medium">Sem requisições</p>
          <p className="text-sm text-muted-foreground/70 mt-1">
            Nenhuma requisição recebida nas últimas 24 horas
          </p>
        </div>
      </div>
    );
  }

  // Renderiza gráfico com dados
  return (
    <div className="rounded-xl border bg-gradient-to-br from-background via-background to-muted/20 p-6 shadow-sm hover:shadow-md transition-shadow duration-300">
      {/* Header com ícone e badges */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-primary/10 text-primary">
            <BarChart3 className="h-5 w-5" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Volume de Requisições</h3>
            <p className="text-xs text-muted-foreground">Últimas 24 horas por hora</p>
          </div>
        </div>
        
        {/* Badges de resumo */}
        <div className="flex gap-2">
          <Badge 
            variant="secondary" 
            className="bg-green-500/10 text-green-600 hover:bg-green-500/20 border-green-500/20"
          >
            <CheckCircle2 className="h-3 w-3 mr-1" />
            {totalSuccess}
          </Badge>
          <Badge 
            variant="secondary" 
            className="bg-red-500/10 text-red-600 hover:bg-red-500/20 border-red-500/20"
          >
            <XCircle className="h-3 w-3 mr-1" />
            {totalError}
          </Badge>
        </div>
      </div>

      {/* Gráfico de barras empilhadas */}
      <ChartContainer config={chartConfig} className="h-[200px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
          >
            <XAxis 
              dataKey="horaLabel"
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              interval="preserveStartEnd"
              tickMargin={8}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              width={30}
            />
            <ChartTooltip
              cursor={{ fill: 'hsl(var(--muted) / 0.3)', radius: 4 }}
              content={<ChartTooltipContent hideLabel />}
            />
            <ChartLegend content={<ChartLegendContent />} />
            
            {/* Barra de Sucesso */}
            <Bar 
              dataKey="sucesso" 
              stackId="volume"
              fill="var(--color-sucesso)"
              radius={[0, 0, 0, 0]}
              name="Sucesso"
            />
            
            {/* Barra de Erro */}
            <Bar 
              dataKey="erro" 
              stackId="volume"
              fill="var(--color-erro)"
              radius={[4, 4, 0, 0]}
              name="Erro"
            />
          </BarChart>
        </ResponsiveContainer>
      </ChartContainer>
    </div>
  );
}
